import {
	useContext, useEffect, useMemo, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../stores/types/types';
import { ConnectedUser } from '../stores/types/connection';
import { VideoStream } from '../components/types/videos';
import { ProgramViewType } from '../stores/types/session';
import { SOCKET_MESSAGE_TYPES } from '../types/constants';
import { SocketContext } from '../contexts/socket';
import logger from '../utils/logger-rum';

// Grace before declaring the share missing: with nothing published we only need to
// absorb shot/user churn, while a published-but-unsubscribed share may still be connecting.
export const NO_SHARE_GRACE_MS = 2000;
export const PENDING_SHARE_GRACE_MS = 10000;

type ShareState = 'ready' | 'pending' | 'none';

/**
 * Resolves a user's screen-share video stream and, when it stays missing past a
 * grace period, alerts hosts over the socket — only from the broadcast embed view
 * (/embed/:meetingId); stream/onsite embeds set a programView and stay silent.
 */
export default function useMissingScreenShareAlert(email: string) {
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const presenterVideos = useSelector((s: GS) => s.VideoReducer.connectedPresenterVideos);
	const isEmbed = useSelector((s: GS) => s.SessionReducer.embed);
	const programView = useSelector((s: GS) => s.SessionReducer.programView);
	const meetingId = useSelector((s: GS) => s.SessionReducer.meeting_id);

	const { socket } = useContext(SocketContext);

	const { screenShareVideo, shareState } = useMemo(() => {
		const connectedUser = connectedUsers.find(
			(user: ConnectedUser) => user.email === email,
		);
		if (!connectedUser?.screenShareWebrtcId) {
			return { screenShareVideo: undefined, shareState: 'none' as ShareState };
		}
		const video = presenterVideos.find(
			(vid: VideoStream) => vid.id === connectedUser.screenShareWebrtcId,
		);
		return {
			screenShareVideo: video,
			shareState: (video ? 'ready' : 'pending') as ShareState,
		};
	}, [connectedUsers, presenterVideos, email]);

	// Read at alert time through a ref so identity churn on these doesn't restart the timer.
	const alertContext = useRef({ socket, meetingId, isEmbed, programView });
	alertContext.current = { socket, meetingId, isEmbed, programView };

	const hasWarned = useRef(false);

	useEffect(() => {
		if (shareState === 'ready') {
			// re-arm the alert for any later outage
			hasWarned.current = false;
			return undefined;
		}
		if (hasWarned.current) return undefined;

		const grace = shareState === 'none' ? NO_SHARE_GRACE_MS : PENDING_SHARE_GRACE_MS;
		const timer = setTimeout(() => {
			hasWarned.current = true;
			const ctx = alertContext.current;
			logger.warn('Screen share video missing after grace period', {
				email,
				meetingId: ctx.meetingId,
				isEmbed: ctx.isEmbed,
				programView: ctx.programView,
				shareState,
				grace,
			});
			if (ctx.socket && ctx.isEmbed && ctx.programView === ProgramViewType.None) {
				ctx.socket.sendMessage(SOCKET_MESSAGE_TYPES.GREENROOM_ALERT, {
					message: 'No Screen Share Available',
					description: `Screen share is not available in embed view for ${email}`,
					duration: 5000,
					type: 'warning',
					host: true, // Only show to hosts
				});
			}
		}, grace);

		return () => clearTimeout(timer);
	}, [shareState, email]);

	return { screenShareVideo };
}
